/* ==========================================================================
   Incipit · Vitrine produits — v1
   • Scroll natif horizontal (flèches + swipe doigt natif sur mobile)
   • Reveal animations (fade + translate Y) sur scroll
   ========================================================================== */

(function () {
  'use strict';

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };


  /* ---------- SLIDER --------------------------------------------------- */

  class IncipitVPSlider {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.slider = section.querySelector('[data-slider]');
      this.track = section.querySelector('[data-slider-track]');
      if (!this.track) return;

      this.bind();

      requestAnimationFrame(() => {
        this.updateArrows();
      });
    }

    bind() {
      // Update flèches au scroll (passif)
      this._onScroll = () => this.updateArrows();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      // Resize
      this._onResize = debounce(() => this.updateArrows(), 120);
      window.addEventListener('resize', this._onResize);

      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObs = new ResizeObserver(this._onResize);
        this._resizeObs.observe(this.section);
      }

      // Empêche le drag natif des images (sinon Firefox/Safari font drag image)
      this.track.querySelectorAll('img').forEach((img) => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      // Flèches (collecte toutes les instances : layout 1 desktop, mobile, layout 2 floating)
      const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
      const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);
      prevBtns.forEach((b) => b.addEventListener('click', () => this.prev()));
      nextBtns.forEach((b) => b.addEventListener('click', () => this.next()));

      // Update après que les images soient chargées
      this.track.querySelectorAll('img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this.updateArrows(), { once: true });
        }
      });
    }

    /**
     * Calcule la largeur d'un "pas" = largeur d'une slide + gap du track.
     * Mesuré dynamiquement pour s'adapter au breakpoint actuel (mobile/desktop).
     */
    getSlideStep() {
      const firstSlide = this.track.querySelector('.incipit-vp__slide');
      if (!firstSlide) return 0;
      const slideWidth = firstSlide.offsetWidth;
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.gap) || parseFloat(trackStyle.columnGap) || 0;
      return slideWidth + gap;
    }

    /**
     * Avance d'UNE slide à la fois (largeur d'une carte + gap).
     * Le scroll-snap aligne automatiquement sur le bord de la prochaine carte.
     */
    next() {
      const step = this.getSlideStep();
      if (step <= 0) return;
      this.track.scrollBy({
        left: step,
        behavior: 'smooth',
      });
    }

    prev() {
      const step = this.getSlideStep();
      if (step <= 0) return;
      this.track.scrollBy({
        left: -step,
        behavior: 'smooth',
      });
    }

    updateArrows() {
      const prevBtns = this.section.querySelectorAll(`[data-slider-prev][data-section="${this.sectionId}"]`);
      const nextBtns = this.section.querySelectorAll(`[data-slider-next][data-section="${this.sectionId}"]`);

      const atStart = this.track.scrollLeft <= 2;
      const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

      // Si le contenu rentre intégralement dans le viewport, on désactive les deux flèches
      const noOverflow = this.track.scrollWidth <= this.track.clientWidth + 4;

      prevBtns.forEach((b) => { b.disabled = atStart || noOverflow; });
      nextBtns.forEach((b) => { b.disabled = atEnd || noOverflow; });
    }
  }


  /* ---------- REVEAL ANIMATIONS --------------------------------------- */

  function initReveal(section) {
    if (typeof IntersectionObserver === 'undefined') {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const prefersReducedMotion = window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
        el.classList.add('is-revealed');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = entry.target;
        let delay = 0;

        if (el.hasAttribute('data-reveal-card')) {
          const track = el.closest('[data-slider-track]');
          if (track) {
            const cards = Array.from(track.querySelectorAll('[data-reveal-card]'));
            const idx = cards.indexOf(el);
            // Base 300ms (après le header) + 80ms par card
            delay = 300 + idx * 80;
          }
        } else {
          delay = parseInt(el.dataset.revealDelay, 10) || 0;
        }

        setTimeout(() => el.classList.add('is-revealed'), delay);
        observer.unobserve(el);
      });
    }, {
      threshold: 0.05,
      rootMargin: '0px 0px -10% 0px',
    });

    section.querySelectorAll('[data-reveal], [data-reveal-card]').forEach((el) => {
      observer.observe(el);
    });
  }


  /* ---------- INIT ---------------------------------------------------- */

  function initSection(section) {
    if (!section || section.dataset.incipitVpInit === 'true') return;
    section.dataset.incipitVpInit = 'true';

    new IncipitVPSlider(section);
    initReveal(section);
  }

  function initAll() {
    document.querySelectorAll('.incipit-vp').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.incipit-vp');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:reorder', initAll);
})();